import React, { memo } from 'react';
import { StyleSheetManager } from 'styled-components';
import stylisRTLPlugin from 'stylis-plugin-rtl';
import styled from 'styled-components';
import { TextDirection } from '../../server/api/country-attributes/country-attributes';

type ContainerPropTypes = React.PropsWithChildren<{
  id?: string;
  background?: { color?: string };
  color?: string;
  accentColor?: string;
  textAlign?: React.CSSProperties['textAlign'];
  direction?: TextDirection;
}> &
  Omit<React.ComponentProps<typeof Background>, 'background'>;

export const Background = styled.section<{ background?: string }>`
  overflow: auto;
  ${({ background }) => (background ? `--background-base: ${background};` : '')}
  background: var(--background-base, inherit);
`;

type ContentPropTypes = {
  color?: string;
  accentColor?: string;
  textAlign?: React.CSSProperties['textAlign'];
};

export const Content = styled.article<ContentPropTypes>`
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
  ${({ color }) => color && `--text-base: ${color};`};
  ${({ accentColor }) =>
    accentColor && `--text-bright-accent: ${accentColor};`};
  color: var(--text-base, inherit);
  strong {
    color: var(--text-bright-accent, inherit);
  }
  padding-top: 35px;
  padding-left: 15px;
  padding-bottom: 35px;
  padding-right: 15px;

  @media (min-width: 992px) {
    width: 970px;
  }
  @media (min-width: 1200px) {
    width: 1170px;
  }
`;

function Container({
  color,
  accentColor,
  id,
  textAlign,
  children,
  background,
  textDirection,
  ...props
}: ContainerPropTypes) {
  const Wrapper = ({
    children: WrapperChildren,
  }: {
    children: React.ReactNode;
  }) => (
    <Background
      data-testid="container"
      id={id}
      background={background?.color || 'transparent'}
      {...props}
      suppressHydrationWarning
    >
      {WrapperChildren}
    </Background>
  );

  const plugins = [];
  if (textDirection === 'rtl') {
    plugins.push(stylisRTLPlugin as any);
  }

  return (
    <StyleSheetManager stylisPlugins={plugins}>
      <Wrapper>
        <Content color={color} accentColor={accentColor} textAlign={textAlign}>
          {children}
        </Content>
      </Wrapper>
    </StyleSheetManager>
  );
}

export default memo(Container);
